import type {
  IFileItem,
  TSubmitFrom,
} from '../../../ChatPage/utils/submitHelpers';
import type { ISelectedSkillTagData } from '../SkillMention/SelectedSkillTag';
import type { ISelectedChartTagData } from '../ChartPopover/components/SelectedChartTag';
import type { ISelectedKnowledgeTagData } from '../KnowledgeBaseMention/components/SelectedKnowledgeTag';
import type {
  IQAInputSubmitServerProps,
  TQAInputOnSubmit,
} from '../types';

/** 页面层 `handleSubmit` 生命周期回调 */
export interface IPageHandleSubmitOptions {
  /** 提交成功时触发 */
  onSuccess?: () => void;
  /** 提交失败时触发 */
  onError?: () => void;
  /** 提交流结束（成功或失败）时触发 */
  onComplete?: () => void;
  /** 提交前触发（如 QAInput 清空 Skill / Chart tag） */
  onBefore?: () => void;
}

/** 页面层 `handleSubmit` 透传给服务端的扩展参数 */
export interface IHandleSubmitServerProps extends IQAInputSubmitServerProps {
  /** Agent 对话模式 */
  mode?: string;
  /** 模型 ID */
  model_id?: string;
  /** 额外 metadata（如 AI Excel） */
  metadata?: Record<string, unknown>;
  /** 前端元数据（Skill 继承、知识库展开 token 等） */
  frontend_metadata?: { isSkillInherited?: boolean; [key: string]: unknown; };
  /** 其它透传字段，兼容 `buildServerParams.serverProps` */
  [key: string]: unknown;
}

/** 输入区引用标签，对应 `buildServerParams` 的 Skill / Chart / 知识库 */
export interface IHandleSubmitMentionConfig {
  /** 当前选中的 Skill tag 列表 */
  selectedSkills?: ISelectedSkillTagData[];
  /** 当前选中的 Chart tag 列表 */
  selectedCharts?: ISelectedChartTagData[];
  /** 当前选中的知识库引用标签列表 */
  selectedKnowledge?: ISelectedKnowledgeTagData[];
}

/**
 * 页面层 `handleSubmit` 对象入参。
 * @template TServerProps - 服务端扩展参数类型，默认 {@link IHandleSubmitServerProps}
 */
export interface IHandleSubmitPayload<
  TServerProps extends IQAInputSubmitServerProps = IHandleSubmitServerProps,
> {
  /** 用户输入或快捷问题文案 */
  value: string;
  /** 本次消息附带的文件列表 */
  fileList?: IFileItem[];
  /** 提交生命周期回调 */
  options?: IPageHandleSubmitOptions;
  /** 提交来源（手动发送 / 猜你想问 / 推荐问题等） */
  from?: TSubmitFrom;
  /** 编辑或刷新时需要删除的消息 ID 列表 */
  deleteMessageIds?: string[];
  /** 服务端扩展参数（如 `use_websearch`） */
  serverProps?: TServerProps;
  /** 输入区引用标签（Skill / Chart / 知识库） */
  mentionConfig?: IHandleSubmitMentionConfig;
  /** 打点扩展参数（仅用于埋点上报，不会发送到后端） */
  logParams?: Record<string, unknown>;
}

/**
 * 页面层 `handleSubmit` 函数签名。
 * @template TServerProps - 服务端扩展参数类型，默认 {@link IHandleSubmitServerProps}
 */
export type TPageHandleSubmit<
  TServerProps extends IQAInputSubmitServerProps = IHandleSubmitServerProps,
> = (payload: IHandleSubmitPayload<TServerProps>) => void;

/** {@link createQAInputOnSubmit} 适配器配置项 */
export interface ICreateQAInputOnSubmitConfig {
  /** 提交前回调（如 ChatPage 滚动到底部） */
  beforeSubmit?: () => void;
  /** 编辑/刷新时需要删除的消息 ID；ChatPage 场景传 `[]` */
  deleteMessageIds?: string[];
}

/**
 * 将页面 `handleSubmit` 适配为 QAInput `onSubmit` 签名。
 * @template TServerProps - 服务端扩展参数类型，需与页面 `handleSubmit` 保持一致
 * @param handleSubmit - ChatPage / Copilot 页面的消息提交函数
 * @param config - 适配器配置（如提交前滚动、deleteMessageIds）
 * @returns QAInput 可直接绑定的 `onSubmit` 回调
 */
export function createQAInputOnSubmit<
  TServerProps extends IQAInputSubmitServerProps = IHandleSubmitServerProps,
>(
  handleSubmit: TPageHandleSubmit<TServerProps>,
  config?: ICreateQAInputOnSubmitConfig,
): TQAInputOnSubmit {
  /** 返回 QAInput 可绑定的 onSubmit，内部转发至页面 handleSubmit */
  return (
    value,
    onBeforeClearTags,
    fileList,
    selectedSkills,
    selectedCharts,
    selectedKnowledge,
    from,
    serverProps,
    logParams,
  ) => {
    config?.beforeSubmit?.();
    handleSubmit({
      value,
      fileList: fileList ?? [],
      options: { onBefore: onBeforeClearTags },
      from: from ?? 'send',
      deleteMessageIds: config?.deleteMessageIds,
      serverProps: serverProps as TServerProps,
      mentionConfig: {
        selectedSkills,
        selectedCharts,
        selectedKnowledge,
      },
      logParams,
    });
  };
}
